import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Table, Btn, Card, Badge, Select } from "@/components/UI";
import { Eye, Upload } from "lucide-react";
import { usePagination, ListPagination } from "@/components/ListPagination";

const STATUS = {
  signed: { label: "İmzalı", level: "ok" },
  generated: { label: "Oluşturuldu · İmza Bekliyor", level: "attention" },
  none: { label: "Sözleşme Yok", level: "missing" },
};

function TemplatePanel() {
  const { user } = useAuth();
  const [tpls, setTpls] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/contract-templates").then(setTpls);
  useEffect(() => { load(); }, []);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !window.confirm(`"${file.name}" yeni revizyon olarak yüklensin mi? Yeni sözleşmeler bu revizyonla üretilir; mevcut sözleşmeler eski revizyonlarında kalır.`)) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.postForm("/contract-templates", fd); load(); } catch (err) { alert(err.response?.data?.detail || "Yükleme başarısız"); }
    setBusy(false);
  };
  return (
    <Card className="p-4" data-testid="fason-template-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold">Sözleşme Şablonu Revizyonları</div>
        {user?.role === "admin" && (
          <label className="cursor-pointer inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700" data-testid="fason-template-upload">
            <Upload size={12}/> {busy ? "Yükleniyor..." : "Yeni Revizyon Yükle"}
            <input type="file" accept="application/pdf" className="hidden" onChange={upload} disabled={busy}/>
          </label>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {tpls.map(t => (
          <button key={t.id} type="button" onClick={() => api.download(`/contract-templates/${t.id}/pdf`, `${t.code}_${t.revision}.pdf`)}
            className="text-xs px-2.5 py-1 rounded-md border border-slate-200 hover:bg-slate-50 flex items-center gap-1" data-testid={`fason-template-${t.revision}`}>
            <Eye size={12}/> {t.code} {t.revision} {t.is_active && <Badge level="ok">Aktif</Badge>}
          </button>
        ))}
      </div>
    </Card>
  );
}

export function FasonContractsTab() {
  const [data, setData] = useState({ rows: [], summary: {} });
  const [filter, setFilter] = useState("");
  const load = useCallback(() => api.get("/gmp/fason-contracts").then(setData), []);
  useEffect(() => { load(); }, [load]);
  const rows = data.rows.filter(r => !filter || r.status === filter);
  const pg = usePagination(rows, "fason-contracts", 100);
  return (
    <div className="space-y-4" data-testid="fason-contracts-tab">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {Object.entries(STATUS).map(([k, s]) => (
          <Card key={k} className={`p-4 cursor-pointer transition-shadow ${filter === k ? "ring-2 ring-teal-500" : ""}`} onClick={() => setFilter(filter === k ? "" : k)} data-testid={`fason-summary-${k}`}>
            <div className="text-xs uppercase tracking-wider text-slate-500">{s.label}</div>
            <div className="text-3xl font-bold">{data.summary[k] || 0}</div>
          </Card>
        ))}
      </div>
      <TemplatePanel/>
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2 gap-2">
          <div className="text-xs text-slate-500">Salt-okunur görünüm. Sözleşme oluşturma ve imzalı asıl yükleme müşteri detayındaki <b>Belgeler</b> bölümünden yapılır.</div>
          <div className="w-full sm:w-56 shrink-0"><Select value={filter} onChange={e => setFilter(e.target.value)} data-testid="fason-status-filter">
            <option value="">Tümü</option>
            {Object.entries(STATUS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
          </Select></div>
        </div>
        <Table testid="fason-contracts-table" columns={[
          { label: "Kod", render: r => <span className="font-mono text-xs">{r.code}</span> },
          { label: "Müşteri", render: r => <Link to={`/customers/${r.customer_id}`} className="text-indigo-600 hover:underline" data-testid={`fason-customer-${r.customer_id}`}>{r.legal_name}</Link> },
          { label: "Durum", render: r => <Badge level={STATUS[r.status].level} testid={`fason-status-${r.customer_id}`}>{STATUS[r.status].label}</Badge> },
          { label: "Sözleşme No", render: r => <span className="font-mono text-xs">{r.contract_no || "—"}</span> },
          { label: "Şablon", render: r => r.template_revision || "—" },
          { label: "Son İşlem", render: r => <span className="text-xs">{r.last_date ? new Date(r.last_date).toLocaleDateString("tr-TR") : "—"}</span> },
          { label: "Belge", render: r => r.last_document_id ? (
            <Btn size="sm" variant="secondary" onClick={() => api.download(`/customer-documents/${r.last_document_id}/file`, `${r.contract_no || "sozlesme"}.pdf`)} data-testid={`fason-open-${r.customer_id}`}><Eye size={12}/></Btn>
          ) : "—" },
        ]} rows={pg.paged} empty="Müşteri yok"/>
        <ListPagination pagination={pg} testid="fason-contracts-pagination"/>
      </Card>
    </div>
  );
}
